<template>
  <section class="relative overflow-hidden py-10 bg-gray-50">
    <div class="container mx-auto px-6">
      <div class="text-center mb-8">
        <img
          :src="require('@/assets/success.png')"
          alt="Success"
          class="mx-auto w-28 h-28"
        />
        <h3 class="mt-6 text-2xl font-semibold text-green-600">
          Ödeme Başarılı!
        </h3>
        <p class="mt-4 text-base text-gray-700">
          Ödeme işleminiz başarıyla tamamlandı. Şimdi sistemde kullanacağınız
          özel erişim adresinizi seçebilirsiniz.
        </p>
      </div>

      <div class="bg-white shadow-lg rounded-lg p-8">
        <form @submit.prevent="onSubmit" class="space-y-6">
          <label
            for="subdomain"
            class="block text-sm font-medium text-gray-700"
          >
            Özel adres Seçimi
          </label>
          <div
            class="flex items-center border rounded-lg overflow-hidden shadow-sm bg-gray-200"
          >
            <input
              id="subdomain"
              v-model="subdomain"
              type="text"
              placeholder="Özel adres girin"
              class="flex-grow px-6 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span class="px-4 py-2 bg-gray-200 text-gray-600">
              .ymmofisim.tr
            </span>
            <div
              class="flex items-center justify-center w-12 h-full bg-gray-200 text-gray-600 rounded-r-lg"
            >
              <span v-if="isCheckingDomain">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <span
                v-if="
                  isCheckingDomain === false &&
                  domainAvailability &&
                  isValidDomain
                "
                class="text-green-500"
              >
                <i class="fas fa-check"></i>
              </span>
              <span
                v-if="
                  isCheckingDomain === false &&
                  (domainAvailability === false || !isValidDomain)
                "
                class="text-red-500"
              >
                <i class="fas fa-times"></i>
              </span>
            </div>
          </div>

          <button
            type="submit"
            class="w-full py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Devam Et
          </button>
        </form>

        <p v-if="errorMessage" class="mt-4 text-red-500 text-sm">
          {{ errorMessage }}
        </p>

        <p
          v-if="domainAvailability === false"
          class="mt-4 text-red-500 text-sm"
        >
          Bu özel adres zaten alınmış. Lütfen başka bir özel adres seçin.
        </p>

        <p class="mt-6 text-sm text-gray-600" v-if="subdomain && isValidDomain">
          Özel erişim adresiniz böyle gözükecek
          <strong>https://{{ subdomain }}.ymmofisim.tr</strong>
        </p>
        <p class="mt-6 text-sm text-gray-600">
          Seçtiğiniz tüm özel adresler
          <strong>ornek.ymmofisim.tr</strong> formatında size özel erisim adresi
          tanımlanacaktır. Lütfen özel adresinizi dikkatli seçiniz.
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import { checkDomain, createTenant } from "@/services/domainService";
import { Toast } from "@/services/toastService";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const subdomain = ref("");
const isValidDomain = ref(false);
const errorMessage = ref("");
const domainAvailability = ref(null);
const isCheckingDomain = ref(false);

const subscriptionId = computed(() => route.query.subscriptionId);

onMounted(() => {
  window.scrollTo(0, 0); // Sayfanın en üstüne kaydırır
});

watch(subdomain, (newValue) => {
  // check if contains invalid characters and show error message
  if (newValue.includes(" ")) {
    errorMessage.value = "Özel adres boşluk içeremez";
    isValidDomain.value = false;
  } else if (newValue.length < 3) {
    errorMessage.value = "Özel adres en az 3 karakter olmalıdır";
    isValidDomain.value = false;
  } else if (newValue.length > 15) {
    errorMessage.value = "Özel adres en fazla 15 karakter olmalıdır";
    isValidDomain.value = false;
  }
  // turkish character check
  else if (/[ğüşıöçİ]/.test(newValue)) {
    errorMessage.value = "Özel adres Türkçe karakter içeremez";
    isValidDomain.value = false;
  } else {
    errorMessage.value = "";
    isValidDomain.value = true;
    processInput(newValue);
  }
});

function debounce(func, delay) {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
}

const processInput = debounce(async () => {
  isCheckingDomain.value = true;
  const result = await checkDomain(subdomain.value, subscriptionId.value);

  isCheckingDomain.value = false;
  if (result) {
    //Toast.success("Domain is available");
    domainAvailability.value = true;
  } else {
    //Toast.error("Domain is not available");
    domainAvailability.value = false;
  }
}, 1000);

const onSubmit = async () => {
  isCheckingDomain.value = true;
  const result = await checkDomain(subdomain.value, subscriptionId.value);
  isCheckingDomain.value = false;
  if (result) {
    domainAvailability.value = true;
    const tenantResult = await createTenant(
      subdomain.value,
      subscriptionId.value
    );

    if (tenantResult.Success) {
      Toast(
        "Başarılı",
        "Özel adres başarıyla seçildi.",
        "success",
        redirectToPanels
      );
    } else {
      Toast(
        "Hata",
        "Özel adres seçimi sırasında bir hata oluştu \n " +
          tenantResult.Message,
        "error"
      );
    }
  } else {
    //Toast.error("Domain is not available");
    domainAvailability.value = false;
    Toast(
      "Hata",
      "Özel adres zaten alınmış. Lütfen başka bir özel adres seçin.",
      "error"
    );
  }
};

const redirectToPanels = () => {
  router.push("/user/domain-selected-successfully/" + subscriptionId.value);
};
</script>

<style scoped>
.container {
  max-width: 720px;
}

input {
  border: none;
  background-color: #f9fafb;
}

span {
  min-width: 150px;
  text-align: center;
}
</style>
